import React, { useState, useEffect } from 'react';
import { BarChart3, Calendar, Users, CreditCard, Download, Package } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import apiService from '../../services/apiService';
import { formatMoney, formatDate } from '../../utils/formatters';
import SucursalFilter from '../../components/filters/SucursalFilter';
import './SalesReportPage.css';

const SalesReportPage = () => {
    const { loading, setLoading, setError, clearError, showToast } = useApp();

    // Estado para filtros
    const [fechaInicio, setFechaInicio] = useState(getTodayDate());
    const [fechaFin, setFechaFin] = useState(getTodayDate());
    const [metodoPago, setMetodoPago] = useState('');
    const [estadoVenta, setEstadoVenta] = useState('');
    const [sucursalId, setSucursalId] = useState('');

    // Estado para datos
    const [reporteVentas, setReporteVentas] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [reportePorMetodo, setReportePorMetodo] = useState([]);
    const [productosMasVendidos, setProductosMasVendidos] = useState([]);
    const [reportePorUsuario, setReportePorUsuario] = useState([]);
    const [activeTab, setActiveTab] = useState('general');

    useEffect(() => {
        loadReportes();
    }, []);

    function getTodayDate() {
        const today = new Date();
        return today.toISOString().split('T')[0];
    }

    const loadReportes = async () => {
        try {
            setLoading(true);
            clearError();

            // Cargar reporte general de ventas
            const resultVentas = await apiService.getReporteVentas(
                fechaInicio,
                fechaFin,
                metodoPago,
                estadoVenta,
                sucursalId
            );

            if (resultVentas.success) {
                setReporteVentas(resultVentas.data.ventas || []);
                setResumen(resultVentas.data.resumen || null);
            }

            // Cargar reporte por método de pago
            const resultMetodo = await apiService.getReporteVentasPorMetodoPago(
                fechaInicio,
                fechaFin,
                sucursalId
            );

            if (resultMetodo.success) {
                setReportePorMetodo(resultMetodo.data || []);
            }

            // Cargar productos más vendidos
            const resultProductos = await apiService.getReporteProductosMasVendidos(
                fechaInicio,
                fechaFin,
                10,
                sucursalId
            );

            if (resultProductos.success) {
                setProductosMasVendidos(resultProductos.data || []);
            }

            // Cargar reporte por usuario
            const resultUsuario = await apiService.getReporteVentasPorUsuario(
                fechaInicio,
                fechaFin,
                sucursalId
            );

            if (resultUsuario.success) {
                setReportePorUsuario(resultUsuario.data || []);
            }

        } catch (error) {
            setError('Error al cargar reportes de ventas');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReport = () => {
        loadReportes();
    };

    const handleQuickDate = (days) => {
        const today = new Date();
        const pastDate = new Date();
        pastDate.setDate(today.getDate() - days);

        setFechaInicio(pastDate.toISOString().split('T')[0]);
        setFechaFin(today.toISOString().split('T')[0]);
    };

    const handleExportReport = () => {
        // TODO: Implementar exportación a CSV/PDF
        showToast('Función de exportación en desarrollo', 'info');
    };

    return (
        <div className="sales-report-page">
            {/* Header */}
            <div className="report-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <BarChart3 size={24} />
                        Reporte de Ventas
                    </h1>
                    <p className="page-description">
                        Análisis detallado de ventas por período, método de pago y productos
                    </p>
                </div>

                <div className="header-actions">
                    <Button
                        onClick={handleExportReport}
                        variant="outline"
                        disabled={loading || reporteVentas.length === 0}
                    >
                        <Download size={18} />
                        Exportar
                    </Button>
                </div>
            </div>

            {/* Filtros */}
            <div className="report-filters">
                <div className="filters-row">
                    <div className="filter-group">
                        <label className="filter-label">
                            <Calendar size={16} />
                            Fecha Inicio
                        </label>
                        <Input
                            type="date"
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">
                            <Calendar size={16} />
                            Fecha Fin
                        </label>
                        <Input
                            type="date"
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">
                            <CreditCard size={16} />
                            Método de Pago
                        </label>
                        <select
                            className="filter-select"
                            value={metodoPago}
                            onChange={(e) => setMetodoPago(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">Todos</option>
                            <option value="efectivo">Efectivo</option>
                            <option value="tarjeta">Tarjeta</option>
                            <option value="transferencia">Transferencia</option>
                            <option value="mixto">Mixto</option>
                        </select>
                    </div>

                    <SucursalFilter
                        value={sucursalId}
                        onChange={setSucursalId}
                        disabled={loading}
                    />

                    <div className="filter-group">
                        <label className="filter-label">Estado</label>
                        <select
                            className="filter-select"
                            value={estadoVenta}
                            onChange={(e) => setEstadoVenta(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">Todos</option>
                            <option value="completada">Completada</option>
                            <option value="cancelada">Cancelada</option>
                        </select>
                    </div>

                    <div className="filter-actions">
                        <Button
                            onClick={handleGenerateReport}
                            disabled={loading}
                        >
                            Generar Reporte
                        </Button>
                    </div>
                </div>

                {/* Quick date filters */}
                <div className="quick-filters">
                    <span className="quick-filters-label">Rápido:</span>
                    <Button
                        variant="ghost"
                        size="small"
                        onClick={() => handleQuickDate(0)}
                        disabled={loading}
                    >
                        Hoy
                    </Button>
                    <Button
                        variant="ghost"
                        size="small"
                        onClick={() => handleQuickDate(7)}
                        disabled={loading}
                    >
                        Últimos 7 días
                    </Button>
                    <Button
                        variant="ghost"
                        size="small"
                        onClick={() => handleQuickDate(30)}
                        disabled={loading}
                    >
                        Últimos 30 días
                    </Button>
                    <Button
                        variant="ghost"
                        size="small"
                        onClick={() => handleQuickDate(90)}
                        disabled={loading}
                    >
                        Últimos 90 días
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="report-tabs">
                <button
                    className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
                    onClick={() => setActiveTab('general')}
                >
                    <BarChart3 size={18} />
                    Ventas Generales
                </button>
                <button
                    className={`tab-button ${activeTab === 'metodos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('metodos')}
                >
                    <CreditCard size={18} />
                    Por Método de Pago
                </button>
                <button
                    className={`tab-button ${activeTab === 'productos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('productos')}
                >
                    <Package size={18} />
                    Productos Más Vendidos
                </button>
                <button
                    className={`tab-button ${activeTab === 'usuarios' ? 'active' : ''}`}
                    onClick={() => setActiveTab('usuarios')}
                >
                    <Users size={18} />
                    Por Usuario
                </button>
            </div>

            {/* Contenido de tabs */}
            <div className="report-content">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando reporte...</p>
                    </div>
                )}

                {!loading && activeTab === 'general' && (
                    <div className="report-table-container">
                        <h3 className="table-title">Listado de Ventas</h3>
                        {reporteVentas.length === 0 ? (
                            <div className="empty-state">
                                <BarChart3 size={48} />
                                <h3>No hay ventas en este período</h3>
                                <p>Intenta seleccionar un rango de fechas diferente</p>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Folio</th>
                                        <th>Cliente</th>
                                        <th>Usuario</th>
                                        <th data-type="numeric">Subtotal</th>
                                        <th data-type="numeric">Total</th>
                                        <th>Método de Pago</th>
                                        <th>Estado</th>
                                        <th>Fecha</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reporteVentas.map((venta) => (
                                        <tr key={venta.id}>
                                            <td>
                                                <span className="folio-badge">{venta.folio}</span>
                                            </td>
                                            <td>{venta.cliente_nombre}</td>
                                            <td>{venta.usuario_nombre}</td>
                                            <td className="numeric-column">{formatMoney(venta.subtotal)}</td>
                                            <td className="numeric-column">
                                                <strong>{formatMoney(venta.total)}</strong>
                                            </td>
                                            <td>
                                                <span className="payment-badge">{venta.metodo_pago}</span>
                                            </td>
                                            <td>
                                                <span className={`status-dot ${venta.estado === 'completada' ? 'active' : 'inactive'}`}>
                                                    {venta.estado === 'completada' ? '● Completada' : '● Cancelada'}
                                                </span>
                                            </td>
                                            <td>{formatDate(venta.fecha_creacion)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {!loading && activeTab === 'metodos' && (
                    <div className="report-table-container">
                        <h3 className="table-title">Ventas por Método de Pago</h3>
                        {reportePorMetodo.length === 0 ? (
                            <div className="empty-state">
                                <CreditCard size={48} />
                                <h3>No hay datos disponibles</h3>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Método de Pago</th>
                                        <th data-type="numeric">Cantidad de Ventas</th>
                                        <th data-type="numeric">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportePorMetodo.map((metodo, index) => (
                                        <tr key={index}>
                                            <td>
                                                <span className="payment-badge">{metodo.metodo_pago}</span>
                                            </td>
                                            <td className="numeric-column">{metodo.cantidad}</td>
                                            <td className="numeric-column">
                                                <strong>{formatMoney(metodo.total)}</strong>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {!loading && activeTab === 'productos' && (
                    <div className="report-table-container">
                        <h3 className="table-title">Top 10 Productos Más Vendidos</h3>
                        {productosMasVendidos.length === 0 ? (
                            <div className="empty-state">
                                <Package size={48} />
                                <h3>No hay datos disponibles</h3>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Posición</th>
                                        <th>Producto</th>
                                        <th data-type="numeric">Cantidad Vendida</th>
                                        <th data-type="numeric">Ventas</th>
                                        <th data-type="numeric">Total Generado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productosMasVendidos.map((producto, index) => (
                                        <tr key={producto.producto_id}>
                                            <td>
                                                <span className="rank-badge">#{index + 1}</span>
                                            </td>
                                            <td>
                                                <strong>{producto.producto_nombre}</strong>
                                            </td>
                                            <td className="numeric-column">{producto.cantidad_vendida}</td>
                                            <td className="numeric-column">{producto.num_ventas}</td>
                                            <td className="numeric-column">
                                                <strong>{formatMoney(producto.total_vendido)}</strong>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {!loading && activeTab === 'usuarios' && (
                    <div className="report-table-container">
                        <h3 className="table-title">Ventas por Usuario</h3>
                        {reportePorUsuario.length === 0 ? (
                            <div className="empty-state">
                                <Users size={48} />
                                <h3>No hay datos disponibles</h3>
                            </div>
                        ) : (
                            <table className="report-table">
                                <thead>
                                    <tr>
                                        <th>Usuario</th>
                                        <th data-type="numeric">Total Ventas</th>
                                        <th data-type="numeric">Total Vendido</th>
                                        <th data-type="numeric">Promedio por Venta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportePorUsuario.map((usuario) => (
                                        <tr key={usuario.usuario_id}>
                                            <td>
                                                <strong>{usuario.usuario_nombre}</strong>
                                            </td>
                                            <td className="numeric-column">{usuario.total_ventas}</td>
                                            <td className="numeric-column">
                                                <strong>{formatMoney(usuario.total_vendido)}</strong>
                                            </td>
                                            <td className="numeric-column">{formatMoney(usuario.promedio_venta)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SalesReportPage;
